import { normaliseShopifyPrice } from '@utils/prices';

import { AML_LIMIT } from '../../../constants/product';
import { CurrencyCode } from '../../../types/Locations';

export type AmlOrderTotal = {
  currencyCode: string;
  totalPrice: number;
};

export type AmlPricedLine = {
  items: { quantity: number; variantId: string }[];
};

export type AmlPricedItem = {
  price: number;
  variantId: string;
};

export type AmlLineSource = {
  frameItems?: ({ quantity: number; variantID: string } | null)[] | null;
  variantId: string | null | undefined;
};

export const buildAmlLines = (sources: AmlLineSource[]): AmlPricedLine[] => {
  return sources.flatMap(({ frameItems, variantId }) => {
    if (!variantId) return [];

    return [
      {
        items: [
          { quantity: 1, variantId },
          ...(frameItems ?? []).flatMap((frameItem) => {
            return frameItem
              ? [
                  {
                    quantity: frameItem.quantity,
                    variantId: frameItem.variantID,
                  },
                ]
              : [];
          }),
        ],
      },
    ];
  });
};

export const exceedsAmlLimit = (total?: AmlOrderTotal | null): boolean => {
  if (!total?.totalPrice) return false;

  return (
    normaliseShopifyPrice(
      total.totalPrice,
      total.currencyCode as CurrencyCode,
    ) > AML_LIMIT
  );
};

export const mostExpensiveLines = <TLine extends AmlPricedLine>(
  lines: TLine[],
  maxQuantity: number,
  pricedItems: AmlPricedItem[],
): TLine[] => {
  const priceByVariantId = new Map(
    pricedItems.map((item) => [item.variantId, item.price]),
  );

  const lineValue = (line: TLine) => {
    return line.items.reduce((total, item) => {
      return (
        total + (priceByVariantId.get(item.variantId) ?? 0) * item.quantity
      );
    }, 0);
  };

  return [...lines]
    .sort((a, b) => lineValue(b) - lineValue(a))
    .slice(0, Math.max(maxQuantity, 1));
};
